import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {InfoCard} from 'src/design/molecules/InfoCard';
import {IconSize, InfoIcon} from 'src/design/atoms/icons/CommonIcons';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {DetailGrid} from 'src/design/atoms/DetailGrid';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {
  PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {
  buildPageNavigation,
} from 'src/app/layout/pageNavigationConfig';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {FormViewRichTextEditorV2} from 'src/app/forms/FormViewRichTextEditor/FormViewRichTextEditorV2';
import {SidebarLinkComplianceStandards} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetGlobalComplianceStandardQuery} from 'src/redux/queries/complianceStandard';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {
  complianceStandardDocumentTitle,
  getTypeColor,
  getTypeLabel,
  pageTitleDict,
} from '../complianceStandardHelpers';

const navigationConfig: PageNavigationConfig = {
  entityName: pageTitleDict.singular,
  entityNamePlural: pageTitleDict.plural,
  listPath: PathUtility.ComplianceStandards(),
  viewPath: id => PathUtility.ComplianceStandardsView(id),
  parentBreadcrumb: {label: 'System', to: PathUtility.AdminSystem()},
};

export const ViewComplianceStandardPage: React.FC = () => {
  useSetDocumentTitle(complianceStandardDocumentTitle);
  const {id} = useParams();

  const {
    data: item,
    isLoading,
    isError,
  } = useGetGlobalComplianceStandardQuery(Number(id), {skip: id === undefined});

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode: 'view',
        id: id,
        itemName: item?.sectionLabel ?? item?.titleNumberLabel,
      }),
    [id, item?.sectionLabel, item?.titleNumberLabel]
  );

  if (isLoading) {
    return (
      <ContentWrapper>
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (isError) {
    return (
      <ContentWrapper>
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />
        <Alert color="red" title="Error">
          Failed to load compliance standard. Please try again later.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkComplianceStandards.icon}
          subtitle={item?.titleNumberLabel}>
          {item?.sectionLabel ?? 'Compliance Standard'}
        </PageHeader.Title>

        <PageHeader.Badges>
          <PageHeader.Badge color={getTypeColor(item?.type)} variant="light">
            {getTypeLabel(item?.type)}
          </PageHeader.Badge>
        </PageHeader.Badges>
      </PageHeader>

      <DetailGrid>
        <DetailGrid.Main>
          <SectionCard title="Content">
            <Box p="md">
              {item?.content ? (
                <FormViewRichTextEditorV2
                  field={{name: 'content', label: '', type: 'richtext'}}
                  value={item.content}
                />
              ) : (
                <Text c="dimmed" size="sm">
                  No content available
                </Text>
              )}
            </Box>
          </SectionCard>
        </DetailGrid.Main>

        <DetailGrid.Sidebar>
          {item && (
            <InfoCard
              title="Standard Info"
              icon={<InfoIcon size={IconSize.sm} />}
              compact>
              {item.internalIdentifier && (
                <InfoCard.Row label="Internal ID" value={item.internalIdentifier} />
              )}
              <InfoCard.Row
                label="Issued"
                value={item.issuedAt?.toString().toShortDate()}
              />
              <InfoCard.Row
                label="Updated"
                value={item.updatedAt?.toString().toShortDate()}
              />
              {item.renderUrl && (
                <InfoCard.Row
                  label="Source"
                  value={
                    <Anchor
                      href={item.renderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      c="blue"
                      size="sm">
                      View Source
                    </Anchor>
                  }
                />
              )}
            </InfoCard>
          )}
        </DetailGrid.Sidebar>
      </DetailGrid>
    </ContentWrapper>
  );
};

export default ViewComplianceStandardPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ComplianceStandardsView(':id'),
  element: <ViewComplianceStandardPage />,
  requireSuperAdmin: true,
  isPrivate: true,
});
